import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, History, Loader2, Search, Calendar, CheckCircle, ChevronDown, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/Input';
import { PRESTATION_PAYMENT_LABELS, PrestationMarkPaidAction } from './CRMModule';

const fmtMoney = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

type SortKey = 'date_desc' | 'date_asc';

// Page dédiée à l'historique complet des rendez-vous (clients Prestation) ou des événements (clients
// Restauration) d'un contact CRM — extraite de la fiche contact (qui n'affichait qu'une liste brute,
// sans recherche ni tri) sur le même modèle que CRMContactDemarchesPage.tsx. Le type d'établissement
// (isPrestationClient) détermine laquelle des deux vues s'affiche, exactement comme sur la fiche contact.
export const CRMContactHistoriquePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPrestationClient = (user as any)?.client_type === 'prestation';
    const [contact, setContact] = React.useState<any | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sort, setSort] = React.useState<SortKey>('date_desc');
    const [expandedId, setExpandedId] = React.useState<string | null>(null);

    const load = React.useCallback(() => {
        if (!id) return;
        moduleApi.getCRMContact(id).then((c: any) => setContact(c)).catch(() => setContact(null)).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    const contactLabel = contact
        ? (contact.type === 'PROFESSIONNEL' ? contact.company_name : `${contact.first_name || ''} ${contact.last_name || ''}`.trim())
        : '';

    const appointments = React.useMemo(() => {
        const list = Array.isArray(contact?.appointments) ? contact.appointments : [];
        const query = q.trim().toLowerCase();
        const filtered = query ? list.filter((a: any) => String(a.prestation_label || '').toLowerCase().includes(query)) : list;
        return [...filtered].sort((a: any, b: any) => {
            const da = new Date(`${a.appointment_date}T${a.start_time || '00:00'}`).getTime();
            const db = new Date(`${b.appointment_date}T${b.start_time || '00:00'}`).getTime();
            return sort === 'date_asc' ? da - db : db - da;
        });
    }, [contact, q, sort]);

    const events = React.useMemo(() => {
        const list = Array.isArray(contact?.events) ? contact.events : [];
        const query = q.trim().toLowerCase();
        const filtered = query ? list.filter((e: any) => String(e.company_name || '').toLowerCase().includes(query)) : list;
        return [...filtered].sort((a: any, b: any) => {
            const da = new Date(a.start_time).getTime(), db = new Date(b.start_time).getTime();
            return sort === 'date_asc' ? da - db : db - da;
        });
    }, [contact, q, sort]);

    return (
        <div className="space-y-6">
            <header className="flex items-center gap-3">
                <button onClick={() => navigate(`/crm/contacts/${id}`)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2">
                        <History size={22} /> {isPrestationClient ? 'Historique des rendez-vous' : 'Historique des Événements'}
                    </h1>
                    <p className="text-content-muted mt-1">{contactLabel || '...'}</p>
                </div>
            </header>

            <div className="flex items-center gap-2 max-w-2xl">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isPrestationClient ? 'Rechercher une prestation...' : 'Rechercher un événement...'} className="pl-8" />
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
                    className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                    <option value="date_desc">Date (récent → ancien)</option>
                    <option value="date_asc">Date (ancien → récent)</option>
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : isPrestationClient ? (
                <div className="space-y-3 max-w-2xl">
                    {appointments.length === 0 && (
                        <p className="text-center text-content-muted py-12 bg-surface rounded-2xl border border-dashed border-border">Aucun rendez-vous trouvé.</p>
                    )}
                    {appointments.map((appt: any) => {
                        const isExpanded = expandedId === appt.id;
                        const payment = PRESTATION_PAYMENT_LABELS[appt.payment_status] || PRESTATION_PAYMENT_LABELS.a_encaisser;
                        const estimatedTotal = Number(appt.total_amount_ttc) > 0 ? Number(appt.total_amount_ttc) : Number(appt.unit_price_ttc || 0) * Number(appt.quantity || 1);
                        let extras: any[] = [];
                        try { extras = JSON.parse(appt.completion_extras || '[]'); } catch {}
                        return (
                            <div key={appt.id} className="bg-surface rounded-2xl border border-border overflow-hidden">
                                <button type="button" onClick={() => setExpandedId(isExpanded ? null : appt.id)}
                                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-surface-soft/60 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-surface-soft rounded-xl flex flex-col items-center justify-center text-content border border-border shrink-0">
                                            <span className="text-[10px] font-bold uppercase opacity-50">{new Date(appt.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'short' })}</span>
                                            <span className="text-lg font-bold leading-none">{new Date(appt.appointment_date + 'T00:00:00').getDate()}</span>
                                        </div>
                                        <div>
                                            <p className="text-content font-bold">{appt.prestation_label}</p>
                                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                                                <span className="text-xs text-content-muted flex items-center gap-1"><Calendar size={12} /> {appt.start_time}</span>
                                                {appt.status === 'termine' ? (
                                                    <span className="text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-lg flex items-center gap-1"><CheckCircle size={10} /> Terminé</span>
                                                ) : (
                                                    <span className="text-[10px] font-black uppercase bg-warning-500/10 text-warning-400 border border-warning-500/20 px-2 py-0.5 rounded-lg">Planifié</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-content">{fmtMoney(estimatedTotal)}</p>
                                            {appt.status === 'termine' && <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded border ${payment.color}`}>{payment.label}</span>}
                                        </div>
                                        <ChevronDown size={16} className={`text-content-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                </button>
                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-5 pb-5 pt-1 border-t border-border space-y-2 text-sm">
                                                <div className="flex justify-between text-content-muted">
                                                    <span>{appt.prestation_label}{appt.quantity > 1 ? ` × ${appt.quantity}` : ''}</span>
                                                    <span>{fmtMoney(Number(appt.unit_price_ttc || 0) * Number(appt.quantity || 1))}</span>
                                                </div>
                                                {extras.map((e: any, i: number) => (
                                                    <div key={i} className="flex justify-between text-content-muted">
                                                        <span>{e.label}</span>
                                                        <span>{fmtMoney(Number(e.unit_price_ttc || 0) * Number(e.quantity || 1))}</span>
                                                    </div>
                                                ))}
                                                {appt.status === 'termine' && (
                                                    <div className="flex justify-between pt-2 border-t border-border font-bold text-content">
                                                        <span>Total facturé</span>
                                                        <span>{fmtMoney(estimatedTotal)}</span>
                                                    </div>
                                                )}
                                                {appt.facture_id && (
                                                    <p className="text-xs text-content-muted italic pt-1">Facture générée automatiquement à la clôture — consultable dans le module Facture.</p>
                                                )}
                                                {appt.status === 'termine' && appt.payment_status !== 'encaisse' && (estimatedTotal - Number(appt.amount_already_paid || 0)) > 0 && (
                                                    <div className="pt-2">
                                                        <PrestationMarkPaidAction
                                                            appt={appt}
                                                            remainingDue={estimatedTotal - Number(appt.amount_already_paid || 0)}
                                                            onUpdated={load}
                                                        />
                                                    </div>
                                                )}
                                                {appt.notes && <p className="text-xs text-content-muted italic pt-1">{appt.notes}</p>}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="space-y-3 max-w-3xl">
                    {events.length === 0 && (
                        <p className="text-center text-content-muted py-12 bg-surface rounded-2xl border border-dashed border-border">Aucun événement trouvé.</p>
                    )}
                    {events.map((event: any) => (
                        <div key={event.id} className="bg-surface rounded-2xl border border-border p-6 flex items-center justify-between hover:border-white/20 transition-all">
                            <div className="flex items-center gap-6">
                                <div className="w-12 h-12 bg-surface-soft rounded-xl flex flex-col items-center justify-center text-content border border-border">
                                    <span className="text-[10px] font-bold uppercase opacity-50">{new Date(event.start_time).toLocaleDateString('fr-FR', { month: 'short' })}</span>
                                    <span className="text-lg font-bold leading-none">{new Date(event.start_time).getDate()}</span>
                                </div>
                                <div>
                                    <p className="text-content font-bold">{event.type === 'PRIVÉ' ? 'Privatisation Particulier' : `Event Pro - ${event.company_name}`}</p>
                                    <div className="flex items-center gap-4 mt-1">
                                        <span className="text-xs text-content-muted flex items-center gap-1">
                                            <Calendar size={12} />
                                            {Array.isArray(event.spaces) ? event.spaces.map((s: any) => s.name || s.id).join(', ') : (event.spaces || 'N/A')}
                                        </span>
                                        <span className="text-xs text-content-muted flex items-center gap-1"><Users size={12} /> {event.num_people} pers.</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-content-muted">{new Date(event.start_time).getFullYear()}</p>
                                <p className="text-xs font-bold text-content mt-1">{new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CRMContactHistoriquePage;
